import React from 'react';
import { X, LayoutList, Sparkles, BookText, FolderPlus, Wand2, PenLine } from 'lucide-react';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';

interface Props {
  categoryName: string;
  onFlashcards: () => void;
  onContextual?: () => void;
  onGenerateText?: () => void;
  onClose: () => void;
  isOwner?: boolean;
  hasCards?: boolean;
  hasMultipleCards?: boolean;
  onAICard?: () => void;
  onAIFolder?: () => void;
  onArrangeWithAI?: () => void;
  onAddText?: () => void;
}

export const PracticePickerModal: React.FC<Props> = ({
  categoryName,
  onFlashcards,
  onContextual,
  onGenerateText,
  onClose,
  isOwner = false,
  hasCards = false,
  hasMultipleCards = false,
  onAICard,
  onAIFolder,
  onArrangeWithAI,
  onAddText,
}) => {
  useBodyScrollLock();
  const handlePick = (fn: () => void) => {
    onClose();
    fn();
  };

  const showAddContent = isOwner && (onAICard || onAIFolder || onArrangeWithAI || onAddText);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-100">
          <div>
            <h2 className="text-base font-semibold text-gray-900">{categoryName}</h2>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-full transition-colors text-gray-400">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-4 sm:px-5 pb-6 pt-4 space-y-5">

          {/* Practice section */}
          {hasCards && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide px-1">Practice</p>
              <button
                type="button"
                onClick={() => handlePick(onFlashcards)}
                className="w-full flex items-center gap-4 p-4 rounded-xl border border-gray-100 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
              >
                <span className="flex-shrink-0 p-2 rounded-lg bg-green-100 text-green-600">
                  <LayoutList className="h-5 w-5" />
                </span>
                <div>
                  <p className="text-sm font-semibold text-gray-900">Flashcards</p>
                  <p className="text-xs text-gray-500 mt-0.5">Review each word and rate how well you know it</p>
                </div>
              </button>

              {onContextual && (
                <button
                  type="button"
                  onClick={() => handlePick(onContextual)}
                  className="w-full flex items-center gap-4 p-4 rounded-xl border border-purple-100 bg-purple-50 hover:bg-purple-100 transition-colors text-left"
                >
                  <span className="flex-shrink-0 p-2 rounded-lg bg-purple-100 text-purple-600">
                    <Sparkles className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Q&amp;A Practice</p>
                    <p className="text-xs text-gray-500 mt-0.5">Answer an AI-generated question using your words</p>
                  </div>
                </button>
              )}

              {onGenerateText && (
                <button
                  type="button"
                  onClick={() => handlePick(onGenerateText)}
                  className="w-full flex items-center gap-4 p-4 rounded-xl border border-indigo-100 bg-indigo-50 hover:bg-indigo-100 transition-colors text-left"
                >
                  <span className="flex-shrink-0 p-2 rounded-lg bg-indigo-100 text-indigo-600">
                    <BookText className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Generate text</p>
                    <p className="text-xs text-gray-500 mt-0.5">Create a story or passage using words from this category</p>
                  </div>
                </button>
              )}
            </div>
          )}

          {/* Add content section */}
          {showAddContent && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide px-1">Add content</p>

              {onAICard && (
                <button
                  type="button"
                  onClick={() => handlePick(onAICard)}
                  className="w-full flex items-center gap-4 p-4 rounded-xl border border-purple-100 bg-purple-50 hover:bg-purple-100 transition-colors text-left"
                >
                  <span className="flex-shrink-0 p-2 rounded-lg bg-purple-100 text-purple-600">
                    <Sparkles className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Create card with AI</p>
                    <p className="text-xs text-gray-500 mt-0.5">Generate a vocabulary card with meanings and examples</p>
                  </div>
                </button>
              )}

              {onAIFolder && (
                <button
                  type="button"
                  onClick={() => handlePick(onAIFolder)}
                  className="w-full flex items-center gap-4 p-4 rounded-xl border border-blue-100 bg-blue-50 hover:bg-blue-100 transition-colors text-left"
                >
                  <span className="flex-shrink-0 p-2 rounded-lg bg-blue-100 text-blue-600">
                    <FolderPlus className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Create folder with AI</p>
                    <p className="text-xs text-gray-500 mt-0.5">Generate a folder and populate it with cards</p>
                  </div>
                </button>
              )}

              {onAddText && (
                <button
                  type="button"
                  onClick={() => handlePick(onAddText)}
                  className="w-full flex items-center gap-4 p-4 rounded-xl border border-teal-100 bg-teal-50 hover:bg-teal-100 transition-colors text-left"
                >
                  <span className="flex-shrink-0 p-2 rounded-lg bg-teal-100 text-teal-600">
                    <PenLine className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Add your own text</p>
                    <p className="text-xs text-gray-500 mt-0.5">Paste or write a text to save to this category</p>
                  </div>
                </button>
              )}

              {onArrangeWithAI && hasMultipleCards && (
                <button
                  type="button"
                  onClick={() => handlePick(onArrangeWithAI)}
                  className="w-full flex items-center gap-4 p-4 rounded-xl border border-amber-100 bg-amber-50 hover:bg-amber-100 transition-colors text-left"
                >
                  <span className="flex-shrink-0 p-2 rounded-lg bg-amber-100 text-amber-600">
                    <Wand2 className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Arrange with AI</p>
                    <p className="text-xs text-gray-500 mt-0.5">Let AI group your cards into folders</p>
                  </div>
                </button>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
};
